<script setup lang="ts">
import { useMediaQueryStore } from '@/stores/media_query_store';
import { ClovyrLogo, GithubIcon, TwitterIcon } from '@clovyr/bed';
import { GridItem, GridSection } from '@clovyr/bed/components/blocks';
import { tagline } from '@clovyr/bed/const';
import { storeToRefs } from 'pinia';

const { isTablet } = storeToRefs(useMediaQueryStore());
</script>

<template>
  <div class="footer">
    <div class="container">
      <div class="top">
        <router-link to="/" class="">
          <ClovyrLogo />
        </router-link>
      </div>
      <!-- end:top -->

      <div class="middle">
        <div v-if="!isTablet" class="left-spacer">&nbsp;</div>

        <div class="summary">
          <div class="heading">What is Clovyr?</div>

          <div class="body">{{ tagline }}</div>
        </div>

        <div class="sitemap">
          <ul class="sitemap__section">
            <li class="sitemap__link">
              <a href="https://clovyr.io/about">about</a>
            </li>

            <li class="sitemap__link">
              <a href="https://clovyr.io/features">features</a>
            </li>

            <li class="sitemap__link">
              <a href="https://clovyr.io/pricing">pricing</a>
            </li>

            <li class="sitemap__link">
              <a href="https://clovyr.io/press">press</a>
            </li>
          </ul>

          <ul class="sitemap__section">
            <li class="sitemap__link">
              <a href="https://clovyr.io/contact">contact</a>
            </li>

            <!-- <li class="sitemap__link">
                <a href="https://clovyr.app/code" target="_blank">try clovyr code beta</a>
              </li> -->

            <li>
              <div class="social-links">
                <div class="social-links__link">
                  <a href="https://github.com/clovyr" target="_blank">
                    <GithubIcon />
                  </a>
                </div>

                <div class="social-links__link">
                  <a href="https://twitter.com/growclovyr" target="_blank">
                    <TwitterIcon />
                  </a>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <!-- end:middle -->

      <GridSection class="bottom">
        <GridItem class="copyright" :md="7">
          <p class="copyright__line">&copy; 2018-2023 Clovyr Co. All Rights Reserved.</p>

          <p class="copyright__line">
            All trademarks, logos, and brands are the property of their respective owners.
          </p>

          <p class="copyright__line">
            All company, product, and service names used on this website are for identification
            purposes only.
          </p>
        </GridItem>

        <GridItem class="legal-links" :md="5">
          <RouterLink class="legal-links__link" to="/privacy-policy">Privacy Policy</RouterLink>
          <a class="legal-links__link" href="https://clovyr.io/terms-of-service"
            >Terms of Service</a
          >
        </GridItem>
      </GridSection>
      <!-- end:bottom -->
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/colors';
@use 'styles/core/layout/grid-vars';
@use 'styles/core/responsive';
@use 'styles/core/typography/fonts';
@use 'styles/vendor/bootstrap/scss/mixins/breakpoints';

.footer {
  background-color: colors.color(background);
  padding: 3rem 1rem;

  > .container {
    display: flex;
    flex-direction: column;

    @include breakpoints.media-breakpoint-up(md) {
      padding-left: calc(1.875rem + 8vw);
      padding-right: calc(1.875rem + 8vw);
    }

    .top {
      :deep(.clovyr-logo) {
        width: 120px;
      }
    }

    .middle {
      display: flex;
      flex-direction: row;

      @include breakpoints.media-breakpoint-down(md) {
        flex-wrap: wrap;
      }

      .left-spacer {
        max-width: 275px;
        width: 100%;
      }

      .summary {
        padding-top: 2em;
        padding-right: 4rem;

        @include breakpoints.media-breakpoint-up(md) {
          width: 50%;
          padding-right: 1rem;
        }

        @include breakpoints.media-breakpoint-up(lg) {
          padding-top: 0;
          padding-right: 5rem;
        }

        .heading {
          @include fonts.font(work, medium, 19, 1.68);

          margin-bottom: 0.75rem;
          font-size: 1.25rem;
          color: colors.color(active);

          @include responsive.media-query(phone) {
            margin-bottom: 0.25rem;
          }
        }

        .body {
          font-size: 1rem;
          color: colors.color(grey, tertiary);
        }
      }

      .sitemap {
        margin-top: grid-vars.space(2);

        @include breakpoints.media-breakpoint-up(md) {
          margin-top: 0;
        }

        @include breakpoints.media-breakpoint-up(lg) {
          width: 50%;
        }

        &__link {
          @include fonts.font(mono, 400, 15, 1.6, true);

          margin-bottom: 0.75rem;
          color: colors.color(action, secondary);

          &:hover {
            color: colors.color(white);
          }
        }

        .social-links {
          margin-top: grid-vars.space(2);

          @include responsive.media-query(tablet) {
            margin-top: grid-vars.space(3);
          }

          &__link {
            display: inline-block;

            :deep(svg) {
              fill: colors.color(action, secondary);
            }

            &:hover {
              :deep(svg) {
                fill: colors.color(white);
              }
            }

            &:not(:last-child) {
              margin-right: grid-vars.space(2.4);
            }
          }
        }
      }
    }

    .bottom {
      margin-top: 4rem;

      :deep(.grid) {
        flex-wrap: wrap;
      }

      .copyright {
        @include fonts.font-size(12);

        line-height: 1.5;
        color: colors.color(grey, secondary);
        padding: 0;

        &__line {
          &:not(:last-child) {
            margin-bottom: grid-vars.space(0.5);

            @include responsive.media-query(mobile) {
              margin-bottom: grid-vars.space(1);
            }
          }
        }
      }

      .legal-links {
        font-size: 0.75rem;
        color: colors.color(grey, tertiary);
        margin-bottom: grid-vars.space(2.9);
        margin-top: grid-vars.space(3);
        padding-left: 0;

        @include breakpoints.media-breakpoint-up(md) {
          padding-left: 4.5rem;
          margin-bottom: 0;
          margin-top: 0;
        }

        &__link {
          @include fonts.font-size(12);

          color: colors.color(grey, tertiary);

          &:not(:last-child) {
            margin-right: grid-vars.space(2);
          }
        }

        &--tablet {
          @include responsive.show-on(tablet);

          margin-top: grid-vars.space(2.9);
        }
      }
    }
  }
}
</style>
